/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2023, Zumper
 */

import { ON_BROWSER } from '@zcode/nr/es/shared/env'
import { calculateBoxForListing } from '@zcode/nr/es/shared/utils'
import createCRTstring from '@zcode/nr/es/shared/utils/createCRTstring'
import deepclone from '@zcode/nr/es/shared/utils/deepclone'
import { getOptimizelyExperimentsOrigin } from '@zcode/nr/es/shared/utils/origin'
import type {
  Filters,
  FloorplanListing,
  Listable,
  NormalFloorplanListingData,
  Property,
} from '@zcode/state-type'
import type { Empty } from '@zcode/utility-types'
import type { O } from 'ts-toolbelt'
import { logger } from '../../../../logger'
import { HOOD_SEARCH, NEARBY_SEARCH, SET_MILES } from '../../constants/index'
import { type SaveSearchOrigin, getOrigin } from './getOrigin'
import { getQueryBedroomCount } from './getQueryBedroomCount/getQueryBedroomCount'
import { getQueryMaxPrice } from './getQueryMaxPrice/getQueryMaxPrice'
import { getQueryMinPrice } from './getQueryMinPrice/getQueryMinPrice'

export type WeirdPartialListable = Pick<Listable, 'address' | 'lat' | 'lng'> &
  Empty<Omit<Listable, 'address' | 'lat' | 'lng'>>

export const createSearchQuery = ({
  type,
  entity,
  origin,
  filters,
  floorplan,
  isMobile = true,
  token,
  variationMap,
}: {
  type: typeof NEARBY_SEARCH | typeof HOOD_SEARCH
  entity: WeirdPartialListable | Listable | Property | null | undefined
  origin?: SaveSearchOrigin
  filters: Partial<Filters> | undefined
  floorplan: FloorplanListing<NormalFloorplanListingData> | null | undefined
  isMobile: boolean
  token: string | undefined
  variationMap: {
    readonly [title: string]: string | null | undefined
  }
}) => {
  const query: O.Writable<Partial<Filters>> & {
    bubbleId?: number
    limit?: number
    featuredLimit?: number
    matching?: number
    offset?: number
    sort?: string[]
    origin?: string
    buildingIds?: number[]
    originUrl?: string
    token?: string
    listingIds?: number[]
  } = filters ? deepclone(filters) : {}

  if (!origin) {
    logger.warn('createSearchQuery requires origin')
  }

  if (entity) {
    if (entity.groupId != null) {
      query.maxPrice = getQueryMaxPrice({ entity, filters, floorplan }) ?? undefined
      query.minPrice = getQueryMinPrice({ entity, filters, floorplan }) ?? undefined
      query.bedrooms = getQueryBedroomCount({ entity, filters, floorplan })
    }

    if (type === HOOD_SEARCH && entity.neighborhood?.cityStateName) {
      query.url = entity.neighborhood.cityStateName
    } else if (type === NEARBY_SEARCH && entity.lat && entity.lng) {
      Object.assign(
        query,
        calculateBoxForListing({
          miles: SET_MILES,
          lat: entity.lat,
          lng: entity.lng,
        })
      )

      if (!query.url && entity.neighborhood?.cityStateName) {
        query.url = entity.neighborhood?.cityStateName
      }
    }
  } else if (filters) {
    delete query.bubbleId
    delete query.limit
    delete query.featuredLimit
    delete query.matching
    delete query.offset
    delete query.sort
    query.availableBefore = filters.availableBefore?.replaceAll('-', '/')
    query.availableAfter = filters.availableAfter?.replaceAll('-', '/')
  }

  if (origin) {
    query.origin = getOrigin({ origin, isMobile })
  }
  query.origin = createCRTstring(query.origin)
  // add optimizely experiments/variations to the origin
  const experimentsOrigin = getOptimizelyExperimentsOrigin(variationMap)
  if (experimentsOrigin) {
    query.origin = `${query.origin},${experimentsOrigin}`
  }

  if (token) {
    query.token = token
  }

  if (ON_BROWSER) {
    query.originUrl = entity?.url ? entity.url : window.location.pathname
  }

  // if user has more than 300 disliked listing w/ 5 alerts, Entity too large happens
  delete query.buildingIds
  delete query.listingIds

  return query
}
